import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Lock, Crown, Film, Download } from "lucide-react";

export default function Gallery() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setItems(r.data))
      .catch((e) => {
        if (e.response?.status === 402) setLocked(true);
        else setItems([]);
      });
  }, []);

  if (locked || (user && user !== false && user.subscription_status !== "active")) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-3xl p-12">
          <div className="w-16 h-16 rounded-2xl bg-accent/15 grid place-items-center mx-auto mb-6 glow-pink">
            <Lock className="w-7 h-7 text-accent" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-3">Your gallery is a Creator perk</h1>
          <p className="text-muted-foreground mb-7 max-w-md mx-auto">
            Upgrade to Creator to permanently save every video you make, revisit your history and download in one click.
          </p>
          <Button data-testid="gallery-upgrade" onClick={() => nav("/pricing")} size="lg" className="rounded-full font-semibold px-8">
            <Crown className="w-4 h-4 mr-2" /> Unlock gallery
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-1">Your Gallery</h1>
      <p className="text-muted-foreground mb-8">Every clip you've created, saved forever.</p>

      {items === null ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <div className="glass rounded-3xl p-14 text-center">
          <Film className="w-10 h-10 mx-auto mb-4 text-muted-foreground opacity-60" />
          <p className="text-muted-foreground">No videos yet. Head to the studio and create your first one.</p>
          <Button onClick={() => nav("/studio")} className="rounded-full mt-5 font-semibold">Open Studio</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
          {items.map((it) => (
            <motion.div key={it.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="glass rounded-2xl overflow-hidden group">
              <video src={mediaUrl(it.video_url)} className="w-full h-52 object-cover bg-black/40" muted loop
                onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} />
              <div className="p-4">
                <p className="text-sm font-medium line-clamp-2 mb-2">{it.prompt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground capitalize">{it.mode} · {it.duration}s</span>
                  <a href={mediaUrl(it.video_url)} target="_blank" rel="noreferrer" className="text-primary hover:text-accent">
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
